package com.mgacreative.hotelru.feature.inventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.mgacreative.hotelru.domain.repository.PurchaseOrder
import com.mgacreative.hotelru.domain.repository.PurchaseOrderItem
import com.mgacreative.hotelru.domain.model.Supplier
import com.mgacreative.hotelru.domain.model.SupplierCategory
import com.mgacreative.hotelru.shared.ui.designsystem.components.HFCard
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n

@Composable
fun PurchasingTab(
    viewModel: PurchasingViewModel
) {
    val orders by viewModel.orders.collectAsState()
    val categories by viewModel.categories.collectAsState()
    val suppliers by viewModel.suppliers.collectAsState()

    var showNewOrderDialog by remember { mutableStateOf(false) }

    if (showNewOrderDialog) {
        NewOrderDialog(
            categories = categories,
            suppliers = suppliers,
            onDismiss = { showNewOrderDialog = false },
            onConfirm = { supplier, items ->
                viewModel.createOrder(supplier, items)
                showNewOrderDialog = false
            }
        )
    }

    Box(modifier = Modifier.fillMaxSize()) {
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            items(orders) { order ->
                OrderCard(
                    order = order,
                    onMarkDelivered = { viewModel.markAsDelivered(order.id) },
                    onExportPdf = { supplier -> viewModel.exportToPdf(supplier) }
                )
            }
            item { Spacer(modifier = Modifier.height(80.dp)) }
        }
        
        FloatingActionButton(
            onClick = { showNewOrderDialog = true },
            modifier = Modifier.align(Alignment.BottomEnd).padding(end = 16.dp, bottom = 80.dp),
            containerColor = Color(0xFF2196F3),
            contentColor = Color.White
        ) {
            Icon(Icons.Default.Add, contentDescription = l10n("New Order"))
        }
    }
}

@Composable
fun OrderCard(order: PurchaseOrder, onMarkDelivered: () -> Unit, onExportPdf: (String) -> Unit) {
    HFCard(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(order.supplier_name, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                    Spacer(modifier = Modifier.width(8.dp))
                    TextButton(onClick = { onExportPdf(order.supplier_name) }, contentPadding = PaddingValues(0.dp)) {
                        Text("PDF", fontSize = 12.sp, color = Color(0xFF2196F3))
                    }
                }
                
                val statusColor = if (order.status == "BEKLIYOR") Color(0xFFF59E0B) else Color(0xFF10B981)
                val statusText = if (order.status == "BEKLIYOR") l10n("Pending") else l10n("Delivered")
                
                Text(statusText, color = statusColor, fontWeight = FontWeight.Medium)
            }
            Spacer(modifier = Modifier.height(8.dp))
            Text(l10n("Amount") + ": \${order.total_amount} ₽", color = Color.Gray)
            
            if (order.status == "BEKLIYOR") {
                Spacer(modifier = Modifier.height(12.dp))
                Button(
                    onClick = onMarkDelivered,
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF10B981))
                ) {
                    Icon(Icons.Default.CheckCircle, contentDescription = null, modifier = Modifier.size(18.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(l10n("Mark as Delivered"))
                }
            }
        }
    }
}

@Composable
fun NewOrderDialog(
    categories: List<SupplierCategory>,
    suppliers: List<Supplier>,
    onDismiss: () -> Unit,
    onConfirm: (String, List<PurchaseOrderItem>) -> Unit
) {
    var selectedCategory by remember { mutableStateOf<SupplierCategory?>(null) }
    var selectedSupplier by remember { mutableStateOf<Supplier?>(null) }
    
    var categoryExpanded by remember { mutableStateOf(false) }
    var supplierExpanded by remember { mutableStateOf(false) }
    
    var itemName by remember { mutableStateOf("") }
    var quantity by remember { mutableStateOf("") }
    var price by remember { mutableStateOf("") }
    
    val items = remember { mutableStateListOf<PurchaseOrderItem>() }
    
    val filteredSuppliers = suppliers.filter { it.category_id == selectedCategory?.id }
    
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = { Text(l10n("Create New Order")) },
        text = {
            Column(modifier = Modifier.fillMaxWidth()) {
                // Category Dropdown
                Box(modifier = Modifier.fillMaxWidth()) {
                    OutlinedButton(
                        onClick = { categoryExpanded = true },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(selectedCategory?.name ?: l10n("Select Category"))
                    }
                    DropdownMenu(expanded = categoryExpanded, onDismissRequest = { categoryExpanded = false }) {
                        categories.forEach { cat ->
                            DropdownMenuItem(
                                text = { Text(cat.name) },
                                onClick = {
                                    selectedCategory = cat
                                    selectedSupplier = null
                                    categoryExpanded = false
                                }
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
                
                // Supplier Dropdown
                Box(modifier = Modifier.fillMaxWidth()) {
                    OutlinedButton(
                        onClick = { supplierExpanded = true },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = selectedCategory != null
                    ) {
                        Text(selectedSupplier?.name ?: l10n("Select Supplier"))
                    }
                    DropdownMenu(expanded = supplierExpanded, onDismissRequest = { supplierExpanded = false }) {
                        filteredSuppliers.forEach { sup ->
                            DropdownMenuItem(
                                text = { Text(sup.name) },
                                onClick = {
                                    selectedSupplier = sup
                                    supplierExpanded = false
                                }
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(16.dp))
                
                Text(l10n("Items") + ":", fontWeight = FontWeight.Bold)
                items.forEachIndexed { index, item ->
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("\${item.quantity}x \${item.item_name} (\${item.unit_price} ₽)")
                        IconButton(onClick = { items.removeAt(index) }) {
                            Icon(Icons.Default.Delete, contentDescription = l10n("Delete"), tint = Color.Red, modifier = Modifier.size(20.dp))
                        }
                    }
                }
                
                Spacer(modifier = Modifier.height(8.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = itemName,
                        onValueChange = { itemName = it },
                        label = { Text(l10n("Item")) },
                        modifier = Modifier.weight(1.5f)
                    )
                    OutlinedTextField(
                        value = quantity,
                        onValueChange = { quantity = it },
                        label = { Text(l10n("Quantity")) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.weight(1f)
                    )
                    OutlinedTextField(
                        value = price,
                        onValueChange = { price = it },
                        label = { Text(l10n("Price")) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.weight(1f)
                    )
                }
                TextButton(onClick = {
                    val qty = quantity.toIntOrNull() ?: 0
                    val prc = price.toDoubleOrNull() ?: 0.0
                    if (itemName.isNotBlank() && qty > 0) {
                        items.add(PurchaseOrderItem(item_name = itemName, quantity = qty, unit_price = prc))
                        itemName = ""
                        quantity = ""
                        price = ""
                    }
                }, modifier = Modifier.align(Alignment.End)) {
                    Text(l10n("+ Add to List"))
                }
            }
        },
        confirmButton = {
            Button(onClick = {
                val sName = selectedSupplier?.name
                if (!sName.isNullOrBlank() && items.isNotEmpty()) {
                    onConfirm(sName, items.toList())
                }
            }) {
                Text(l10n("Create"))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text(l10n("Cancel")) }
        }
    )
}

